import PropTypes from 'prop-types';
import { memo, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'store';
import moment from 'moment';
import { isDate } from 'lodash';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import {
    Card,
    CardContent,
    Grid,
    LinearProgress,
    Button,
    Typography,
    linearProgressClasses,
    TextField,
    Box,
    Skeleton
} from '@mui/material';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DesktopDateRangePicker } from '@mui/x-date-pickers-pro';

import { getMonthlyInteractions, getCustomRangeProviderCareStats } from 'store/slices/account';
import { humanFormattedDecimalHours } from 'utils/humanSecondsFormatter';

const RANGE_MODE_WEEK = 'week';
const RANGE_MODE_TODAY = 'today';
const RANGE_MODE_CUSTOM = 'custom';

// styles
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 30,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.light : '#fff'
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.main
    }
}));

const CardStyle = styled(Card)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
    marginBottom: '22px',
    overflow: 'hidden',
    position: 'relative'
}));

/*
2 boxes --> logged care time for the selected range (this week / today / custom),
with a progress bar against the weekly productivity goal in the week view
the box below is just monthly number of touches
ORG LEVEL
*/

// ==============================|| PROGRESS BAR WITH LABEL ||============================== //

function LinearProgressWithLabel({ targetHours = 0, hoursComplete, value, ...others }) {
    const theme = useTheme();

    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Typography
                            variant="caption"
                            sx={{ color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800] }}
                        >
                            {/* Care session time logged <br /> */}
                            {hoursComplete} / {`${targetHours.toFixed(2)}`} hours
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Typography variant="caption" color="inherit">{`${Math.round(value)}%`}</Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <BorderLinearProgress variant="determinate" value={value > 100 ? 100 : value} {...others} />
            </Grid>
        </Grid>
    );
}

LinearProgressWithLabel.propTypes = {
    value: PropTypes.number,
    hoursComplete: PropTypes.number,
    targetHours: PropTypes.number
};

// ==============================|| PROGRESS BAR SKELETON ||============================== //

function CareStatsProgressSkeleton() {
    return (
        <Grid container direction="column" spacing={1} sx={{ mt: 1.5 }}>
            <Grid item>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton variant="text" width={90} />
                    </Grid>
                    <Grid item>
                        <Skeleton variant="text" width={30} />
                    </Grid>
                </Grid>
            </Grid>
            <Grid item>
                <Skeleton variant="rounded" height={10} sx={{ borderRadius: 30 }} />
            </Grid>
        </Grid>
    );
}

// ==============================|| SIDEBAR - MENU CARD ||============================== //

const TodayCareStatsCards = () => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const startOfMonthDefault = moment().startOf('month');
    // use the beginning of the day like the mui picker
    const endOfMonthDefault = moment().endOf('month').startOf('day');

    const [rangeMode, setRangeMode] = useState(RANGE_MODE_WEEK);
    const [dateRange, setDateRange] = useState([startOfMonthDefault.toDate(), endOfMonthDefault.toDate()]);
    const organizationMeta = useSelector((state) => state.account.organizationMeta);
    const accountData = useSelector((state) => state.account);

    const handleDateSelectionChanged = (newValue) => {
        setDateRange(newValue);
    };

    // goal weeks are ISO weeks (Monday start) to match the productivity goal API;
    // all ranges are half-open [fromTS, toTS): the end is the start of the next period
    function getTimestampsForActiveRange() {
        if (rangeMode === RANGE_MODE_WEEK) {
            return [moment().startOf('isoWeek').valueOf(), moment().startOf('isoWeek').add(1, 'week').valueOf()];
        }
        if (rangeMode === RANGE_MODE_TODAY) {
            return [moment().startOf('day').valueOf(), moment().startOf('day').add(1, 'day').valueOf()];
        }
        const start = dateRange?.[0];
        const end = dateRange?.[1];
        if (isDate(start) && isDate(end)) {
            // the picker returns the start of the last selected day
            return [start.getTime(), moment(end).startOf('day').add(1, 'day').valueOf()];
        }
        return null;
    }

    function refreshCareStatsForActiveRange() {
        const timestamps = getTimestampsForActiveRange();
        if (timestamps) {
            // the goal only applies when the range is the current goal week (week + daily views)
            const weekStartDate = rangeMode === RANGE_MODE_CUSTOM ? undefined : moment().startOf('isoWeek').format('YYYY-MM-DD');
            dispatch(getCustomRangeProviderCareStats(timestamps[0], timestamps[1], weekStartDate));
        }
    }

    useEffect(() => {
        dispatch(getMonthlyInteractions());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationMeta]);

    // useLayoutEffect so the loading state is dispatched before the browser paints —
    // otherwise switching ranges flashes the previous range's data (still loaded) for one frame
    useLayoutEffect(() => {
        refreshCareStatsForActiveRange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rangeMode, dateRange, organizationMeta]);

    useEffect(() => {
        if (accountData.customRangeProviderCareStats.needsRefresh) {
            refreshCareStatsForActiveRange();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountData.customRangeProviderCareStats.needsRefresh]);

    const careStats = accountData.customRangeProviderCareStats;
    const patientsCaredFor = accountData.monthlyInteractions.month.patientsCaredFor;
    /*
    seconds / 60 / 60 --> convert to hours
    hours / weeklyGoalHours --> convert to percentage of target
    percentage --> 100 for display in progress bar
    */
    const weeklyGoalHours = careStats.custom.weeklyGoalHours;
    const hoursComplete = Math.round((careStats.custom.secondsLogged / 60 / 60) * 100) / 100;
    const percentageComplete = weeklyGoalHours > 0 ? Math.round((hoursComplete / weeklyGoalHours) * 100) : 0;
    // daily goal is the weekly goal spread over 5 workdays
    const dailyGoalHours = weeklyGoalHours / 5;
    const dailyPercentageComplete = dailyGoalHours > 0 ? Math.round((hoursComplete / dailyGoalHours) * 100) : 0;

    return (
        <>
            <CardStyle>
                <CardContent sx={{ p: 2 }}>
                    <Grid item>
                        <Typography
                            variant="h4"
                            sx={{
                                color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                            }}
                        >
                            My logged care
                        </Typography>
                    </Grid>
                    <Grid container justifyContent="flex-start">
                        <Grid item sx={{ marginTop: 1, marginBottom: 1 }}>
                            <Button
                                disableElevation
                                variant={rangeMode === RANGE_MODE_WEEK ? 'contained' : 'text'}
                                size="small"
                                sx={{ color: 'inherit', '&.MuiButton-contained': { color: '#FFFFFF' } }}
                                onClick={() => setRangeMode(RANGE_MODE_WEEK)}
                            >
                                Weekly
                            </Button>
                            <Button
                                disableElevation
                                variant={rangeMode === RANGE_MODE_TODAY ? 'contained' : 'text'}
                                size="small"
                                sx={{ color: 'inherit', '&.MuiButton-contained': { color: '#FFFFFF' } }}
                                onClick={() => setRangeMode(RANGE_MODE_TODAY)}
                            >
                                Daily
                            </Button>
                            <Button
                                disableElevation
                                variant={rangeMode === RANGE_MODE_CUSTOM ? 'contained' : 'text'}
                                size="small"
                                sx={{ color: 'inherit', '&.MuiButton-contained': { color: '#FFFFFF' } }}
                                onClick={() => setRangeMode(RANGE_MODE_CUSTOM)}
                            >
                                Custom
                            </Button>
                        </Grid>
                    </Grid>
                    {rangeMode === RANGE_MODE_WEEK && (
                        <>
                            {careStats.loaded && (
                                <>
                                    <LinearProgressWithLabel
                                        targetHours={weeklyGoalHours}
                                        hoursComplete={hoursComplete}
                                        value={percentageComplete}
                                    />
                                    <Grid item>
                                        <Typography
                                            variant="subtitle1"
                                            color={theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900'}
                                            sx={{ opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 }}
                                        >
                                            You have completed {humanFormattedDecimalHours(careStats.custom.secondsLogged)} this week.
                                        </Typography>
                                        <Typography
                                            variant="subtitle1"
                                            color={theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900'}
                                            sx={{ opacity: theme.palette.mode === 'dark' ? 1 : 0.6 }}
                                        >
                                            Your goal resets Monday at 12:00 AM
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {!careStats.loaded && (
                                <>
                                    <CareStatsProgressSkeleton />
                                    <Grid item>
                                        <Skeleton variant="text" sx={{ marginTop: 1 }} />
                                        <Skeleton variant="text" width="60%" />
                                        <Skeleton variant="text" width="80%" />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                    {rangeMode === RANGE_MODE_TODAY && (
                        <>
                            {careStats.loaded && (
                                <>
                                    <LinearProgressWithLabel
                                        targetHours={dailyGoalHours}
                                        hoursComplete={hoursComplete}
                                        value={dailyPercentageComplete}
                                    />
                                    <Grid item>
                                        <Typography
                                            variant="subtitle1"
                                            color={theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900'}
                                            sx={{ opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 }}
                                        >
                                            You have completed {humanFormattedDecimalHours(careStats.custom.secondsLogged)} today.
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {!careStats.loaded && (
                                <>
                                    <CareStatsProgressSkeleton />
                                    <Grid item>
                                        <Skeleton variant="text" sx={{ marginTop: 1 }} />
                                        <Skeleton variant="text" width="60%" />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                    {rangeMode === RANGE_MODE_CUSTOM && (
                        <>
                            {careStats.loaded && (
                                <>
                                    <Grid container justifyContent="flex-start" sx={{ marginTop: 1 }}>
                                        <Grid item>
                                            <LocalizationProvider
                                                dateAdapter={AdapterDateFns}
                                                localeText={{ start: 'Start Date', end: 'End Date' }}
                                            >
                                                <DesktopDateRangePicker
                                                    value={dateRange}
                                                    onChange={() => {}}
                                                    onAccept={handleDateSelectionChanged}
                                                    renderInput={(startProps, endProps) => (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                {...startProps}
                                                                sx={{
                                                                    input: {
                                                                        paddingLeft: '6px !important',
                                                                        paddingRight: '6px !important'
                                                                    }
                                                                }}
                                                                inputProps={{ ...startProps.inputProps, readOnly: true }}
                                                            />
                                                            <br />
                                                            <Box sx={{ mx: 1 }}> - </Box>
                                                            <TextField
                                                                size="small"
                                                                {...endProps}
                                                                sx={{
                                                                    input: {
                                                                        paddingLeft: '6px !important',
                                                                        paddingRight: '6px !important'
                                                                    }
                                                                }}
                                                                inputProps={{ ...endProps.inputProps, readOnly: true }}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            </LocalizationProvider>
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="subtitle1"
                                            color={theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900'}
                                            sx={{ opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 }}
                                        >
                                            You have completed {humanFormattedDecimalHours(careStats.custom.secondsLogged)} for the select
                                            date range.
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {!careStats.loaded && (
                                <>
                                    <Grid container justifyContent="flex-start" sx={{ marginTop: 1 }}>
                                        <Grid item>
                                            <Skeleton variant="rounded" width={220} height={40} />
                                        </Grid>
                                    </Grid>
                                    <Grid item>
                                        <Skeleton variant="text" sx={{ marginTop: 1 }} />
                                        <Skeleton variant="text" width="60%" />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </CardContent>
            </CardStyle>
            {accountData.monthlyInteractions.loaded && (
                <CardStyle>
                    <CardContent sx={{ p: 2 }}>
                        <Grid item>
                            <Typography
                                variant="h4"
                                sx={{
                                    color: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary[800]
                                }}
                            >
                                Monthly unique <br /> patient interactions
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="subtitle1"
                                color={theme.palette.mode === 'dark' ? 'textSecondary' : 'grey.900'}
                                sx={{ opacity: theme.palette.mode === 'dark' ? 1 : 0.6, marginTop: 1 }}
                            >
                                You have cared for {patientsCaredFor} patients so far this month.
                            </Typography>
                        </Grid>
                    </CardContent>
                </CardStyle>
            )}
        </>
    );
};

export default memo(TodayCareStatsCards);
